import { useAuth } from '../auth/AuthContext';

/**
 * Qué secciones abre quien está en sesión (Administración → Permisos, 0.55.0). La lista maestra y la regla viven en el
 * API (api/src/lib/permisos.ts); aquí sólo se sabe qué RUTA es de qué sección, para el menú (TopBar), las rutas
 * (ProtectedRoute) y "la primera sección que sí puede abrir". Las secciones llegan con el login y con `auth/session`.
 */
export type SeccionId =
  | 'resumen' | 'fichas' | 'alertas' | 'analitica' | 'marketing' | 'galeria' | 'comisiones' | 'equipo'
  | 'app_pacientes' | 'formularios' | 'reglas' | 'admin';

/** A dónde lleva cada sección, en el orden en que se busca "la primera que sí abre" (Fichas no es una pantalla de inicio). */
export const RUTA_SECCION: [SeccionId, string][] = [
  ['resumen', '/'], ['alertas', '/alertas'], ['analitica', '/analitica'], ['marketing', '/marketing'], ['galeria', '/marketing?tab=galeria'],
  ['comisiones', '/comisiones'], ['equipo', '/equipo'], ['app_pacientes', '/app-pacientes'], ['formularios', '/formularios'],
  ['reglas', '/config'], ['admin', '/admin'],
];

/**
 * Lo de siempre por rol (el mismo DEFAULT_POR_ROL del API): sólo para una sesión que llegó SIN `secciones` (una pestaña
 * abierta durante el despliegue). Quien decide de verdad es el servidor.
 */
const LEGADO: Record<string, SeccionId[]> = {
  medico: ['resumen', 'fichas', 'comisiones'], cosmetologa: ['resumen', 'fichas', 'comisiones'], recepcion: ['resumen', 'fichas', 'comisiones'],
  asesora: ['resumen', 'fichas', 'comisiones'], marketing: ['galeria', 'app_pacientes'],
};
const TODAS = RUTA_SECCION.map(([s]) => s).concat(['fichas']);

export function seccionesEfectivas(user: { role: string; secciones?: string[] } | null | undefined): SeccionId[] {
  if (!user) return [];
  if (user.role === 'admin') return TODAS;
  if (Array.isArray(user.secciones)) return user.secciones as SeccionId[];
  return LEGADO[user.role] || ['resumen', 'fichas'];
}

/** La primera pantalla que esta cuenta sí puede abrir; sin ninguna, "Mi cuenta" (ésa es de todos). */
export function primeraRuta(secciones: string[]): string {
  const r = RUTA_SECCION.find(([s]) => secciones.includes(s));
  return r ? r[1] : '/mi-cuenta';
}

/** `puede('app_pacientes')` en cualquier pantalla. `esAdmin`: lo que sigue siendo de admin dentro de una sección. */
export function usePermisos() {
  const { user } = useAuth();
  const secciones = seccionesEfectivas(user);
  return {
    secciones,
    esAdmin: user?.role === 'admin',
    puede: (s: SeccionId | SeccionId[]) => (Array.isArray(s) ? s : [s]).some((x) => secciones.includes(x)),
    inicio: primeraRuta(secciones),
  };
}
